// eventManager-dashboard.component.ts
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, Observable, combineLatest, finalize, map, startWith, shareReplay, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventsService, Event as EventModel } from '../events/events.service';
import { UpcomingActivitiesComponent } from '../unified-activities/upcoming-activities.component';
import { MatButtonModule } from '@angular/material/button';

export interface EventDashboardMetrics {
  events: {
    total: number;
    upcoming: number;
    past: number;
  };
  attendees: {
    total: number;
  };
}

@Component({
  selector: 'app-event-manager-dashboard',
  imports: [
    MatIconModule, MatTooltipModule, MatCardModule,
    ReactiveFormsModule, MatSelectModule, MatProgressSpinnerModule,
    AsyncPipe, UpcomingActivitiesComponent, MatButtonModule
  ],
  templateUrl: './eventManager-dashboard.component.html',
  styleUrl: './eventManager-dashboard.component.scss'
})
export class EventManagerDashboardComponent implements OnInit {
  timeRange = new FormControl("Today");

  private eventsService = inject(EventsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly metricsLoadingSubject = new BehaviorSubject<boolean>(true);
  metricsLoading$ = this.metricsLoadingSubject.asObservable();

  private allEvents$ = this.eventsService.getVisibleEvents().pipe(
    tap(() => this.metricsLoadingSubject.next(true)),
    map(response => response.data ?? []),
    finalize(() => this.metricsLoadingSubject.next(false)),
    shareReplay(1)
  );

  metrics$: Observable<EventDashboardMetrics> = combineLatest([
    this.allEvents$,
    this.timeRange.valueChanges.pipe(startWith(this.timeRange.value))
  ]).pipe(
    map(([events, range]) => this.computeMetrics(this.filterByRange(events, range ?? 'Today')))
  );

  totalEvents$!: Observable<number>;
  upcomingEvents$!: Observable<number>;
  pastEvents$!: Observable<number>;
  totalAttendees$!: Observable<number>;

  ngOnInit() {
    this.metrics$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.totalEvents$ = this.metrics$.pipe(map(m => m.events.total));
    this.upcomingEvents$ = this.metrics$.pipe(map(m => m.events.upcoming));
    this.pastEvents$ = this.metrics$.pipe(map(m => m.events.past));
    this.totalAttendees$ = this.metrics$.pipe(map(m => m.attendees.total));
  }

  private filterByRange(events: EventModel[], range: string): EventModel[] {
    if (range === 'All') {
      return events;
    }

    const now = new Date();
    const rangeStart = new Date(now);

    switch (range) {
      case 'Today':
        rangeStart.setHours(0, 0, 0, 0);
        break;
      case 'Week':
        rangeStart.setDate(now.getDate() - 7);
        break;
      case 'Month':
        rangeStart.setMonth(now.getMonth() - 1);
        break;
      default:
        return events;
    }

    return events.filter(ev => {
      const start = ev.start ? new Date(ev.start).getTime() : NaN;
      return !isNaN(start) && start >= rangeStart.getTime();
    });
  }

  private computeMetrics(events: EventModel[]): EventDashboardMetrics {
    const now = Date.now();

    let upcoming = 0;
    let past = 0;
    let attendees = 0;

    for (const ev of events) {
      const start = ev.start ? new Date(ev.start).getTime() : NaN;
      if (!isNaN(start) && start >= now) {
        upcoming++;
      } else {
        past++;
      }
      attendees += ev.participants?.length ?? 0;
    }

    return {
      events: {
        total: events.length,
        upcoming,
        past
      },
      attendees: {
        total: attendees
      }
    };
  }

  manageEvents(): void {
    this.router.navigate(['/events']);
  }

  createEvent(): void {
    this.router.navigate(['/events'], { queryParams: { action: 'create' } });
  }
}